// Single source of truth for all semantic color tokens
// Used by GlobalStyles.js (CSS variables) and ThemeContext.js (JS colors object)

export const lightColors = {
  // Surfaces — warm cream tones
  bgPrimary: '#faf7f2',
  bgSecondary: '#f3efe8',
  bgElevated: '#ffffff',
  bgInput: '#ffffff',

  // Text — warm dark tones
  textPrimary: '#2a2520',
  textSecondary: '#6b5744',
  textMuted: '#a09080',
  textInverse: '#faf7f2',

  // Brand — warm terracotta
  brandPrimary: '#c4704b',
  brandSecondary: '#d4845f',
  brandLight: '#f5e8e0',
  brandShadow: 'rgba(196, 112, 75, 0.3)',
  brandShadowStrong: 'rgba(196, 112, 75, 0.4)',
  brandFocus: 'rgba(196, 112, 75, 0.15)',

  // Borders — warm
  borderPrimary: '#e0d8cc',
  borderLight: '#e8e0d4',
  borderLighter: '#f0ece4',

  // Shadows — warmer
  shadow: 'rgba(42, 37, 32, 0.08)',
  shadowStrong: 'rgba(42, 37, 32, 0.14)',

  // Success — earthy green (semantic)
  successBg: '#e8efe4',
  successBorder: '#c4d8bc',
  successText: '#3a6040',
  successAccent: '#5a8a5e',
  successLight: '#eaf0e6',

  // Error — warm red
  errorBg: '#f5e0dc',
  errorBorder: '#e8c4bc',
  errorText: '#7a3028',
  errorAccent: '#c45c5c',
  errorLight: '#f8ece8',

  // Navbar — warm cream
  navBg: 'rgba(250, 247, 242, 0.95)',
  navBgScrolled: 'rgba(250, 247, 242, 0.98)',
  navShadow: 'rgba(42, 37, 32, 0.08)',
  navShadowScrolled: 'rgba(42, 37, 32, 0.12)',
  navMobileBg: '#faf7f2',

  // Chat widget — warm tones
  chatBubbleBg: '#f3efe8',
  chatInputBorder: '#e0d8cc',
  chatDataCardBorder: '#e0d8cc',
  chatDataCardHeaderBg: '#f3efe8',
  chatRowBorder: '#f0ece4',
  chatPlaceholder: '#a09080',
  chatTypingDot: '#a09080',
  chatStatusAvailableBg: '#e8efe4',
  chatStatusAvailableText: '#3a6040',
  chatStatusPendingBg: '#faf0e0',
  chatStatusPendingText: '#c4704b',
  chatStatusCancelledBg: '#f3efe8',
  chatStatusCancelledText: '#8a7a6a',
  chatAdminBadgeBg: 'rgba(212, 168, 87, 0.3)',
  chatRoomCode: '#8a7a6a',

  // Booking form — warm
  formGuestSectionBg: '#f5f0e8',
  formGuestSectionBorder: '#e0d8cc',
  formGuestRowBg: '#faf7f2',
  formRemoveBtn: '#b84030',

  // Language popup
  popupBg: '#faf7f2',

  // Unsubscribe
  emailDisplayBg: '#f3efe8',

  // Subscription form (non-footer)
  subscriptionBg: '#faf7f2',

  // Login
  googleBtnBg: '#4285f4',
  googleBtnHover: '#357ae8',

  // Admin-specific tokens — warm organic palette
  adminPrimary: '#c4704b',
  adminPrimaryHover: '#d4845f',
  adminTitleColor: '#2a2520',
  adminSidebar: '#2a2520',
  adminHeaderBg: '#f5f0e8',
  adminContentBg: '#f3efe8',
  adminBorderColor: '#e0d8cc',
  adminMenuItemBg: 'transparent',
  adminMenuSelectedBg: 'rgba(196, 112, 75, 0.12)',
  adminMenuSelectedColor: '#c4704b',
  adminMenuHoverBg: 'rgba(196, 112, 75, 0.08)',
  adminMenuItemColor: '#f5f0e8',
  adminAvatarGradient: 'linear-gradient(135deg, #c4704b 0%, #d4a857 100%)',
  adminCodeBg: '#ede5d8',

  // Calendar
  calendarWeekendBg: '#f5f0e8',
  calendarDragBg: '#f0e0d0',
  calendarDragEndBg: '#e0c8b0',
  calendarTodayBg: '#c4704b',
  calendarTodayText: '#ffffff',
  calendarCurrentMonthText: '#2a2520',
  calendarOtherMonthText: '#b8a898',
  calendarCountText: '#8a7a6a',
  calendarOverflowText: '#c4704b',
  calendarGridBorder: '#e8e0d4',
  calendarHeaderText: '#6b5744',
  calendarHoverBg: 'rgba(196,112,75,0.06)',
  calendarOffDateBg: 'rgba(196,80,60,0.08)',
  calendarOffDateBorder: 'rgba(196,80,60,0.25)',
  calendarOffDateText: '#b84030',

  // Admin misc
  discountColor: '#b84030',
  hiddenIndicator: '#8a3a3a',
  fbCardBg: '#f0ece4',
  fbCardBorder: '#d0c8b8',
  fbCardTitle: '#6b5744',
};

export const darkColors = {
  // Surfaces — warm dark earth tones
  bgPrimary: '#1a1714',
  bgSecondary: '#211e1a',
  bgElevated: '#2a2520',
  bgInput: '#332e28',

  // Text — warm off-whites
  textPrimary: '#e8e0d4',
  textSecondary: '#b8a898',
  textMuted: '#7a6e60',
  textInverse: '#1a1714',

  // Brand — warm terracotta
  brandPrimary: '#d4845f',
  brandSecondary: '#e09878',
  brandLight: '#3a2e24',
  brandShadow: 'rgba(212, 132, 95, 0.25)',
  brandShadowStrong: 'rgba(212, 132, 95, 0.35)',
  brandFocus: 'rgba(212, 132, 95, 0.15)',

  // Borders — warm dark
  borderPrimary: '#3d3630',
  borderLight: '#332e28',
  borderLighter: '#2a2520',

  // Shadows
  shadow: 'rgba(0, 0, 0, 0.25)',
  shadowStrong: 'rgba(0, 0, 0, 0.4)',

  // Success — earthy green
  successBg: '#222e24',
  successBorder: '#3a5238',
  successText: '#8bc49a',
  successAccent: '#6aaa78',
  successLight: '#222e24',

  // Error — warm red
  errorBg: '#2e2220',
  errorBorder: '#4a3430',
  errorText: '#e0a098',
  errorAccent: '#d08078',
  errorLight: '#2a2018',

  // Navbar — warm dark
  navBg: 'rgba(26, 23, 20, 0.95)',
  navBgScrolled: 'rgba(26, 23, 20, 0.98)',
  navShadow: 'rgba(0, 0, 0, 0.25)',
  navShadowScrolled: 'rgba(0, 0, 0, 0.4)',
  navMobileBg: '#1a1714',

  // Chat widget — warm dark
  chatBubbleBg: '#2a2520',
  chatInputBorder: '#3d3630',
  chatDataCardBorder: '#3d3630',
  chatDataCardHeaderBg: '#211e1a',
  chatRowBorder: '#332e28',
  chatPlaceholder: '#7a6e60',
  chatTypingDot: '#7a6e60',
  chatStatusAvailableBg: '#222e24',
  chatStatusAvailableText: '#8bc49a',
  chatStatusPendingBg: '#302818',
  chatStatusPendingText: '#e0b878',
  chatStatusCancelledBg: '#2a2520',
  chatStatusCancelledText: '#b8a898',
  chatAdminBadgeBg: 'rgba(212, 168, 87, 0.2)',
  chatRoomCode: '#b8a898',

  // Booking form — warm dark
  formGuestSectionBg: '#2a2520',
  formGuestSectionBorder: '#3d3630',
  formGuestRowBg: '#332e28',
  formRemoveBtn: '#d08078',

  // Language popup
  popupBg: '#2a2520',

  // Unsubscribe
  emailDisplayBg: '#211e1a',

  // Subscription form (non-footer)
  subscriptionBg: '#2a2520',

  // Login
  googleBtnBg: '#5b9bf0',
  googleBtnHover: '#6da8f5',

  // Admin-specific tokens — warm organic dark
  adminPrimary: '#d4845f',
  adminPrimaryHover: '#e09878',
  adminTitleColor: '#f5f0e8',
  adminSidebar: '#1a1714',
  adminHeaderBg: '#2a2520',
  adminContentBg: '#1e1b18',
  adminBorderColor: '#3d3630',
  adminMenuItemBg: 'transparent',
  adminMenuSelectedBg: 'rgba(212, 132, 95, 0.15)',
  adminMenuSelectedColor: '#d4845f',
  adminMenuHoverBg: 'rgba(212, 132, 95, 0.1)',
  adminMenuItemColor: '#c4b8a8',
  adminAvatarGradient: 'linear-gradient(135deg, #d4845f 0%, #d4a857 100%)',
  adminCodeBg: '#2a2520',

  // Calendar
  calendarWeekendBg: '#241f1a',
  calendarDragBg: '#3a2e22',
  calendarDragEndBg: '#4a3828',
  calendarTodayBg: '#d4845f',
  calendarTodayText: '#ffffff',
  calendarCurrentMonthText: '#e8e0d4',
  calendarOtherMonthText: '#5a5048',
  calendarCountText: '#a89888',
  calendarOverflowText: '#d4845f',
  calendarGridBorder: '#332e28',
  calendarHeaderText: '#a89888',
  calendarHoverBg: 'rgba(212,132,95,0.08)',
  calendarOffDateBg: 'rgba(196,80,60,0.12)',
  calendarOffDateBorder: 'rgba(196,80,60,0.3)',
  calendarOffDateText: '#e08070',

  // Admin misc
  discountColor: '#e09080',
  hiddenIndicator: '#e09080',
  fbCardBg: '#2a2520',
  fbCardBorder: '#4a3a28',
  fbCardTitle: '#d4a857',
};

// Light BTN styles (current values)
export const LIGHT_BTN = {
  edit:     { background: '#faf0e0', borderColor: '#e8d0a0', color: '#7a5c2c' },
  confirm:  { background: '#e8f0e4', borderColor: '#a8c8a0', color: '#3d6635' },
  checkin:  { background: '#e0ece8', borderColor: '#98c8b8', color: '#2d5a4a' },
  checkout: { background: '#f0e8e0', borderColor: '#d0b898', color: '#7a5c3c' },
  cancel:   { background: '#f8e8e0', borderColor: '#e0b098', color: '#8a4030' },
  export:   { background: '#e8e4d8', borderColor: '#c8c0a8', color: '#5a5040' },
  fb:       { background: '#e0e4f0', borderColor: '#a0a8d0', color: '#3a3a6a' },
  zalo:     { background: '#e0ecf0', borderColor: '#90c0d0', color: '#2a5060' },
};

export const DARK_BTN = {
  edit:     { background: '#302818', borderColor: '#4a3c28', color: '#f0d088' },
  confirm:  { background: '#1e2e20', borderColor: '#304830', color: '#88c890' },
  checkin:  { background: '#1e2a28', borderColor: '#2e4440', color: '#80c0b0' },
  checkout: { background: '#2e2418', borderColor: '#4a3a28', color: '#d4a870' },
  cancel:   { background: '#2e1e1a', borderColor: '#4a3028', color: '#e0a080' },
  export:   { background: '#28241e', borderColor: '#403828', color: '#c8b880' },
  fb:       { background: '#222230', borderColor: '#3a3a58', color: '#a0a8d0' },
  zalo:     { background: '#1e2830', borderColor: '#2e4050', color: '#80b8d0' },
};

// Light STAT styles (current values)
export const LIGHT_STAT = {
  total:      { card: { background: '#f5f0e8', borderColor: '#e0d8cc' }, color: '#6b5744' },
  pending:    { card: { background: '#fef3c7', borderColor: '#fde68a' }, color: '#92400e' },
  confirmed:  { card: { background: '#e8f0e4', borderColor: '#c8d8c0' }, color: '#3d6635' },
  checked_in: { card: { background: '#e0ece8', borderColor: '#b8d4c8' }, color: '#2d5a4a' },
  checked_out:{ card: { background: '#f0e8e0', borderColor: '#dcc8b0' }, color: '#7a5c3c' },
  cancelled:  { card: { background: '#f5e0e0', borderColor: '#e8c0c0' }, color: '#8a3a3a' },
  active:     { card: { background: '#e8f0e4', borderColor: '#c8d8c0' }, color: '#3d6635' },
  recent:     { card: { background: '#f5f0e8', borderColor: '#e0d8cc' }, color: '#6b5744' },
  count:      { card: { background: '#f0e8e0', borderColor: '#dcc8b0' }, color: '#7a5c3c' },
  unsub:      { card: { background: '#f5e0e0', borderColor: '#e8c0c0' }, color: '#8a3a3a' },
};

export const DARK_STAT = {
  total:      { card: { background: '#2a2520', borderColor: '#3d3630' }, color: '#d4a857' },
  pending:    { card: { background: '#302818', borderColor: '#4a3c28' }, color: '#f0d888' },
  confirmed:  { card: { background: '#1e2e20', borderColor: '#304830' }, color: '#88c890' },
  checked_in: { card: { background: '#1e2a28', borderColor: '#2e4440' }, color: '#80c0b0' },
  checked_out:{ card: { background: '#2e2418', borderColor: '#4a3a28' }, color: '#d4a870' },
  cancelled:  { card: { background: '#2e1e1e', borderColor: '#4a3030' }, color: '#e09090' },
  active:     { card: { background: '#1e2e20', borderColor: '#304830' }, color: '#88c890' },
  recent:     { card: { background: '#2a2520', borderColor: '#3d3630' }, color: '#d4a857' },
  count:      { card: { background: '#2e2418', borderColor: '#4a3a28' }, color: '#d4a870' },
  unsub:      { card: { background: '#2e1e1e', borderColor: '#4a3030' }, color: '#e09090' },
};

export const LIGHT_BOOKING_BAR_COLORS = {
  pending:     { bg: 'rgba(245,158,11,0.15)', dot: '#f59e0b', text: '#92400e' },
  confirmed:   { bg: 'rgba(16,185,129,0.15)', dot: '#10b981', text: '#065f46' },
  checked_in:  { bg: 'rgba(6,182,212,0.15)',  dot: '#06b6d4', text: '#155e75' },
  checked_out: { bg: 'rgba(139,92,246,0.15)', dot: '#8b5cf6', text: '#5b21b6' },
  cancelled:   { bg: 'rgba(156,163,175,0.25)', dot: '#9ca3af', text: '#6b7280' },
};

export const DARK_BOOKING_BAR_COLORS = {
  pending:     { bg: 'rgba(245,158,11,0.25)', dot: '#fbbf24', text: '#fcd34d' },
  confirmed:   { bg: 'rgba(16,185,129,0.25)', dot: '#34d399', text: '#6ee7b7' },
  checked_in:  { bg: 'rgba(6,182,212,0.25)',  dot: '#22d3ee', text: '#67e8f9' },
  checked_out: { bg: 'rgba(139,92,246,0.25)', dot: '#a78bfa', text: '#c4b5fd' },
  cancelled:   { bg: 'rgba(156,163,175,0.2)', dot: '#6b7280', text: '#9ca3af' },
};

// Room-based color palette for calendar bars (10 distinct hues)
export const LIGHT_ROOM_COLORS = [
  { bg: 'rgba(59,130,246,0.18)',  border: '#3b82f6', text: '#1e40af' },   // blue
  { bg: 'rgba(239,68,68,0.15)',   border: '#ef4444', text: '#991b1b' },    // red
  { bg: 'rgba(16,185,129,0.18)',  border: '#10b981', text: '#065f46' },    // emerald
  { bg: 'rgba(245,158,11,0.18)',  border: '#f59e0b', text: '#92400e' },    // amber
  { bg: 'rgba(139,92,246,0.18)',  border: '#8b5cf6', text: '#5b21b6' },    // violet
  { bg: 'rgba(236,72,153,0.15)',  border: '#ec4899', text: '#9d174d' },    // pink
  { bg: 'rgba(6,182,212,0.18)',   border: '#06b6d4', text: '#155e75' },    // cyan
  { bg: 'rgba(234,88,12,0.15)',   border: '#ea580c', text: '#9a3412' },    // orange
  { bg: 'rgba(101,163,13,0.18)',  border: '#65a30d', text: '#3f6212' },    // lime
  { bg: 'rgba(168,85,247,0.15)',  border: '#a855f7', text: '#6b21a8' },    // purple
];

export const DARK_ROOM_COLORS = [
  { bg: 'rgba(59,130,246,0.3)',   border: '#60a5fa', text: '#93bbfd' },    // blue
  { bg: 'rgba(239,68,68,0.25)',   border: '#f87171', text: '#fca5a5' },    // red
  { bg: 'rgba(16,185,129,0.3)',   border: '#34d399', text: '#6ee7b7' },    // emerald
  { bg: 'rgba(245,158,11,0.3)',   border: '#fbbf24', text: '#fcd34d' },    // amber
  { bg: 'rgba(139,92,246,0.3)',   border: '#a78bfa', text: '#c4b5fd' },    // violet
  { bg: 'rgba(236,72,153,0.25)',  border: '#f472b6', text: '#f9a8d4' },    // pink
  { bg: 'rgba(6,182,212,0.3)',    border: '#22d3ee', text: '#67e8f9' },    // cyan
  { bg: 'rgba(234,88,12,0.25)',   border: '#fb923c', text: '#fdba74' },    // orange
  { bg: 'rgba(101,163,13,0.3)',   border: '#84cc16', text: '#bef264' },    // lime
  { bg: 'rgba(168,85,247,0.25)',  border: '#c084fc', text: '#d8b4fe' },    // purple
];

// Map token names to CSS variable names
export const cssVariableMap = Object.keys(lightColors).reduce((acc, key) => {
  // Convert camelCase to kebab-case
  const cssName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
  acc[key] = `--${cssName}`;
  return acc;
}, {});
